import { Bot, Coins, Plus, Send } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MessageBubble, { Message } from './MessageBubble';

interface Props {
    messages: Message[];
    inputMessage: string;
    isAgentThinking: boolean;
    isPreviewReady: boolean;
    isPublishing: boolean;
    currentActivity: string | null;
    tokenBalance: number | undefined;
    isFirstTurn: boolean;
    onInputChange: (value: string) => void;
    onSend: () => void;
    onNewChat: () => void;
}

export default function ChatPanel({
    messages,
    inputMessage,
    isAgentThinking,
    isPreviewReady,
    isPublishing,
    currentActivity,
    tokenBalance,
    isFirstTurn,
    onInputChange,
    onSend,
    onNewChat,
}: Props) {
    const navigate = useNavigate();
    const bottomRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to bottom whenever messages change
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isAgentThinking]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !e.shiftKey && !isAgentThinking && isPreviewReady && !isPublishing) {
            onSend();
        }
    }, [isAgentThinking, isPreviewReady, isPublishing, onSend]);

    const isDisabled = isAgentThinking || !isPreviewReady || isPublishing;

    const placeholder = !isPreviewReady
        ? 'Loading…'
        : isPublishing
            ? 'Publishing in progress…'
            : isAgentThinking
                ? 'AI is working…'
                : 'Describe what you want to change…';

    const formatBalance = (n: number) => {
        if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace('.0', '') + 'M';
        if (n >= 1_000) return (n / 1_000).toFixed(1).replace('.0', '') + 'K';
        return n.toString();
    };

    const showLowTokenBanner = tokenBalance !== undefined && tokenBalance < 1000 && tokenBalance > 0;
    const showWarmUpHint = isFirstTurn && messages.length <= 1 && !isAgentThinking;

    return (
        <div className="flex flex-col w-full h-full overflow-hidden bg-white">
            {/* Chat header — assistant identity + New chat (a chat-scoped action) */}
            <div className="flex-shrink-0 flex items-center justify-between gap-2 h-12 px-3 sm:px-4 border-b border-gray-200 bg-white">
                <div className="flex items-center gap-2 min-w-0">
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Bot className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-sm font-semibold text-gray-900 truncate">Design Assistant</span>
                </div>
                <button
                    onClick={onNewChat}
                    disabled={isAgentThinking || isPublishing}
                    title="Start a new chat — clears this conversation"
                    className="flex items-center gap-1.5 h-9 px-3 text-sm font-medium text-primary bg-primary/5 hover:bg-primary/10 border border-primary/20 rounded-lg transition-colors active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                >
                    <Plus className="w-4 h-4" />
                    <span>New chat</span>
                </button>
            </div>

            {/* Message list */}
            <div className="flex-1 overflow-y-auto overscroll-contain space-y-3 bg-gray-50 p-3 sm:p-4 scroll-pb-24">
                {/* Low token warning */}
                {showLowTokenBanner && (
                    <div className="sticky top-0 z-20 -mx-1 mb-1">
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 shadow-sm">
                            <p className="text-sm text-amber-800 font-medium">
                                <span className="mr-1">⚠</span>
                                Low tokens: {formatBalance(tokenBalance!)} left.
                                <button
                                    onClick={() => navigate('/tokens/purchase')}
                                    className="ml-2 text-amber-700 hover:text-amber-900 underline font-semibold"
                                >
                                    Buy more →
                                </button>
                            </p>
                        </div>
                    </div>
                )}

                {/* Warm-up hint — shown before first send */}
                {showWarmUpHint && (
                    <div className="animate-in fade-in duration-500">
                        <div className="bg-primary/5 border border-primary/15 rounded-xl p-3">
                            <p className="text-xs text-primary/90 leading-relaxed flex items-start gap-2">
                                <span className="text-sm leading-none mt-0.5">✨</span>
                                <span>
                                    <span className="font-semibold">Your first request may use more tokens</span> as the AI loads its context. Follow-up requests are cached and cost less.
                                </span>
                            </p>
                        </div>
                    </div>
                )}

                {messages.map(msg => (
                    <MessageBubble key={msg.id} message={msg} />
                ))}

                {/* Thinking indicator */}
                {isAgentThinking && (
                    <div className="flex justify-start animate-in slide-in-from-bottom-1 duration-200">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mr-2 mt-0.5">
                            <Bot className="w-4 h-4 text-primary animate-pulse" />
                        </div>
                        <div className="max-w-[82%] sm:max-w-md px-3.5 py-2.5 rounded-2xl rounded-bl-md bg-white border border-gray-200/70 shadow-sm">
                            <p className="text-xs font-semibold text-gray-700 mb-0.5">
                                {isFirstTurn ? 'Getting ready…' : 'Working on it…'}
                            </p>
                            <p className="text-xs text-gray-500">
                                {currentActivity
                                    ? currentActivity
                                    : isFirstTurn
                                        ? 'Setting things up — the next request will be faster.'
                                        : 'Applying your changes.'}
                            </p>
                            <div className="flex items-center gap-1 mt-2">
                                {[0, 150, 300].map(d => (
                                    <span key={d} className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: `${d}ms` }} />
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Scroll anchor */}
                <div ref={bottomRef} />
            </div>

            {/* Input bar — pinned at the bottom, thumb-friendly, respects the notch */}
            <div className="flex-shrink-0 border-t border-gray-200 bg-white px-3 pt-2.5 pb-[max(10px,env(safe-area-inset-bottom))]">
                {/* Token balance strip */}
                <div className="flex items-center justify-between mb-2">
                    <span className="flex items-center gap-1.5 text-xs text-gray-500">
                        <Coins className="w-3.5 h-3.5 text-amber-500" />
                        {tokenBalance !== undefined ? formatBalance(tokenBalance) : '---'} tokens
                    </span>
                    <button
                        onClick={() => navigate('/tokens/purchase')}
                        className="text-xs text-primary hover:opacity-80 font-semibold"
                    >
                        + Buy
                    </button>
                </div>

                <div className="flex items-end gap-2">
                    <input
                        type="text"
                        inputMode="text"
                        enterKeyHint="send"
                        className="flex-1 min-h-[44px] px-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none text-base placeholder:text-gray-400 transition-shadow disabled:opacity-60 disabled:cursor-not-allowed"
                        placeholder={placeholder}
                        value={inputMessage}
                        onChange={e => onInputChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={isDisabled}
                    />
                    <button
                        onClick={onSend}
                        disabled={!inputMessage.trim() || isDisabled}
                        aria-label="Send message"
                        className="h-11 w-11 flex-shrink-0 flex items-center justify-center bg-primary text-white rounded-2xl shadow-sm transition-all hover:opacity-90 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <Send className="w-5 h-5" />
                    </button>
                </div>
            </div>
        </div>
    );
}
